import { Link } from "react-router-dom";
import { services } from "@/data/services";
import { Phone, Mail, MessageCircle, MapPin, Clock } from "lucide-react";
import linkedinIcon from "@/assets/linkedin-icon.png";
import logoRinqel from "@/assets/logo-rinqel.png";

const Footer = () => {
  return (
    <footer className="border-t border-border bg-muted/50">
      <div className="container py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="inline-flex items-center gap-3">
              <img src={logoRinqel} alt="Rinqel logo" width={256} height={256} className="h-10 w-auto object-contain" loading="lazy" decoding="async" />
              <span className="text-2xl font-bold tracking-tight">
                <span className="text-gradient">Rinqel</span>
                <span className="text-muted-foreground">.it</span>
              </span>
            </Link>
            <p className="text-sm leading-relaxed text-muted-foreground">
              IT zonder gedoe. Vast laag maandbedrag, geen verrassingen. Gewoon IT die werkt.
            </p>
          </div>

          {/* Diensten */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Diensten</h4>
            <ul className="space-y-2">
              {services.map((service) => {
                // Scherpere anchor-text voor interne linkwaarde, zonder de visuele layout te wijzigen.
                const anchorMap: Record<string, string> = {
                  internet: "Zakelijk internet",
                  netwerken: "Zakelijke netwerken",
                  mobiel: "Zakelijk mobiel",
                  telefonie: "Zakelijke telefonie",
                  werkplek: "Managed werkplek",
                  software: "Software licenties",
                  hardware: "Zakelijke hardware",
                  "alles-in-een": "Alles-in-één IT",
                };
                return (
                  <li key={service.slug}>
                    <Link
                      to={`/diensten/${service.slug}`}
                      className="text-sm text-muted-foreground transition-colors hover:text-primary"
                    >
                      {anchorMap[service.slug] || service.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Links */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Snel naar</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/configurator" className="text-sm text-muted-foreground transition-colors hover:text-primary">
                  Prijsconfigurator
                </Link>
              </li>
              <li>
                <Link to="/klanten" className="text-sm text-muted-foreground transition-colors hover:text-primary">
                  Klanten
                </Link>
              </li>
              <li>
                <Link to="/praktijkvoorbeelden" className="text-sm text-muted-foreground transition-colors hover:text-primary">
                  Praktijkvoorbeelden
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-muted-foreground transition-colors hover:text-primary">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/zoeken" className="text-sm text-muted-foreground transition-colors hover:text-primary">
                  Zoeken
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-4 w-4 text-rinqel-blue" aria-hidden="true" />
                <a href="tel:+31858223400" aria-label="Bel Rinqel op 085-8223400" className="hover:text-primary">085-8223400</a>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <MessageCircle className="h-4 w-4 text-rinqel-green" aria-hidden="true" />
                <a href="https://api.whatsapp.com/send?phone=31858223400" target="_blank" rel="noopener noreferrer" aria-label="Stuur Rinqel een WhatsApp-bericht" className="hover:text-primary">WhatsApp</a>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4 text-[#eb4f05]" aria-hidden="true" />
                <a className="hover:text-primary" aria-label="Mail Rinqel" href="mailto:contact@rinqel.it">contact@rinqel.it</a>
              </li>
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 mt-0.5 text-rinqel-blue" aria-hidden="true" />
                <a href="https://maps.app.goo.gl/fjCgnkUTePEbzge27" target="_blank" rel="noopener noreferrer" aria-label="Bekijk Rinqel op Google Maps" className="hover:text-primary">Rijksweg 42a, 1906 BJ Limmen</a>
              </li>
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4 mt-0.5 text-primary" aria-hidden="true" />
                <div>
                  <p>Ma–Vr 09:00 – 18:00</p>
                  <p className="text-xs text-muted-foreground/70">Storing buiten kantooruren? Klanten met contract 24/7 bereikbaar.</p>
                </div>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <img loading="lazy" decoding="async" src={linkedinIcon} alt="Rinqel op LinkedIn" className="h-4 w-4 rounded-[2px]" />
                <a href="https://www.linkedin.com/company/rinqel" target="_blank" rel="noopener noreferrer" aria-label="Bekijk Rinqel op LinkedIn" className="hover:text-primary">LinkedIn.com/company/Rinqel</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-border pt-8 md:flex-row">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Rinqel. Alle rechten voorbehouden.
          </p>
          <p className="text-xs text-muted-foreground">
            Maandelijks opzegbaar · Geen uurtje-factuurtje · Gewoon eerlijke IT
          </p>
        </div>
      </div>
    </footer>);

};

export default Footer;